import { trackMP } from '@/shared/mp';

import { getStep, getTour } from './registry';
import { TourState } from './types';

/**
 * Furthest progress computed from a history. Only used to backfill the stored mark when
 * migrating legacy state — the live value is stored (see furthestWith), because the history is
 * capped and can evict the high-water step.
 */
export const furthestOf = (tourId: string, stepHistory: string[] = []) =>
  stepHistory.reduce(
    (best, id) => {
      const n = getStep(tourId, id)?.analytics?.stepNumber;
      return n !== undefined && n > best.furthestStepIndex ? { furthestStepIndex: n, furthestStepId: id } : best;
    },
    { furthestStepIndex: -1, furthestStepId: undefined as string | undefined },
  );

/**
 * The stored high-water mark, with `enteringStepId` folded in — events are emitted before the
 * advance is dispatched, so the step being entered must still count.
 */
export const furthestWith = (tourId: string, tourState?: TourState, enteringStepId?: string) => {
  const stored = {
    furthestStepIndex: tourState?.furthestStepNumber ?? -1,
    furthestStepId: tourState?.furthestStepId,
  };
  const n = enteringStepId ? getStep(tourId, enteringStepId)?.analytics?.stepNumber : undefined;
  return n !== undefined && n > stored.furthestStepIndex ? { furthestStepIndex: n, furthestStepId: enteringStepId } : stored;
};

/**
 * Emit a 'guided-tour' event. The tour's analytics `name` comes from its registry entry; a
 * tour without one emits nothing, so adding a tour never creates a new event stream silently.
 */
export const trackTour = (tourId: string, action: string, extra?: Record<string, unknown>): void => {
  const tourName = getTour(tourId)?.name;
  if (!tourName) return;
  trackMP('guided-tour', { tourName, action, ...extra });
};

/**
 * Emit the auto-start 'triggered' event, unless the tour opts out via `trackStart: false`
 * (a continuation tour reports under the same name but is not a new start).
 */
export const trackTourStart = (tourId: string): void => {
  if (getTour(tourId)?.trackStart === false) return;
  trackTour(tourId, 'triggered');
};

/**
 * A step that sits off the main line (the paused steps) carries this instead of a position.
 * It can never advance the furthest mark, and in events it defers to where the user actually is.
 */
export const OFF_LINE_STEP_NUMBER = -1;

const positionOf = (tourId: string, stepId: string): number | undefined => {
  const n = getStep(tourId, stepId)?.analytics?.stepNumber;
  return n === OFF_LINE_STEP_NUMBER ? undefined : n;
};

/**
 * The step number to report for an event. An off-line step has no position of its own, so it
 * inherits the last real one from the run: a break taken at step 12 reports 12, which is what
 * makes "where do users pause?" answerable without joining against the preceding event.
 * `stepId` still distinguishes the two (`paused-ocean` vs `O2`).
 */
export const effectiveStepNumber = (tourId: string, stepId: string, stepHistory: string[] = []): number | undefined => {
  const own = positionOf(tourId, stepId);
  if (own !== undefined) return own;

  for (let i = stepHistory.length - 1; i >= 0; i -= 1) {
    const previous = positionOf(tourId, stepHistory[i]);
    if (previous !== undefined) return previous;
  }
  // Nothing better to say: fall back to whatever the step declared (-1, or nothing at all).
  return getStep(tourId, stepId)?.analytics?.stepNumber;
};

/** Event payload for a step: its analytics bag plus the furthest-progress mark. */
export const stepPayload = (tourId: string, stepId: string, tourState?: TourState) => {
  const stepNumber = effectiveStepNumber(tourId, stepId, tourState?.stepHistory);
  return {
    stepId,
    ...getStep(tourId, stepId)?.analytics,
    ...(stepNumber !== undefined && { stepNumber }),
    ...furthestWith(tourId, tourState, stepId),
  };
};

export default trackTour;
